/**
 * ExpandableList Component
 * Reusable component for displaying expandable lists of affected entities or relationships
 */

import React, { useState } from 'react';

export interface ExpandableListItem {
  id: string;
  displayName: string;
  attributes: Record<string, any>;
}

export interface ExpandableListProps {
  frictionId: string;
  frictionType: 'entity_friction' | 'relationship_friction' | 'regulatory_framework' | 'regulatory_friction';
  items: ExpandableListItem[];
  count: number;
  isLoading?: boolean;
  onItemClick: (itemId: string) => void;
  onExpand?: () => void;
  customLabel?: string; // Optional custom label override
}

export const ExpandableList: React.FC<ExpandableListProps> = ({
  frictionId,
  frictionType,
  items,
  count,
  isLoading = false,
  onItemClick,
  onExpand,
  customLabel
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    
    // Call onExpand callback when expanding (for lazy loading)
    if (newExpandedState && onExpand) {
      onExpand();
    }
  };

  const getListLabel = () => {
    // Use custom label if provided
    if (customLabel) {
      return `${customLabel} (${count})`;
    }
    
    // Default labels based on friction type
    if (frictionType === 'relationship_friction') {
      return `Affected Relationships (${count})`;
    }
    return `Affected Entities (${count})`;
  };

  return (
    <div className="expandable-list" style={{ marginTop: '8px', marginBottom: '8px' }}>
      <button
        onClick={handleToggle}
        className="expandable-list-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          border: '1px solid #ddd',
          borderRadius: '4px',
          background: '#f8f9fa',
          cursor: 'pointer',
          width: '100%',
          textAlign: 'left',
          fontSize: '14px',
          fontWeight: '500',
          color: '#495057'
        }}
      >
        <span style={{ fontSize: '12px' }}>
          {isExpanded ? '▼' : '▶'}
        </span>
        <span>{getListLabel()}</span>
      </button>

      {isExpanded && (
        <div
          className="expandable-list-content"
          style={{
            marginTop: '4px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            background: '#fff',
            maxHeight: '300px',
            overflowY: 'auto'
          }}
        >
          {isLoading ? (
            <div style={{ padding: '16px', textAlign: 'center', color: '#6c757d' }}>
              Loading...
            </div>
          ) : count === 0 ? (
            <div
              className="no-matches"
              style={{
                padding: '16px',
                textAlign: 'center',
                color: '#6c757d',
                fontStyle: 'italic'
              }}
            >
              No matches
            </div>
          ) : (
            <ul
              className="expandable-list-items"
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0
              }}
            >
              {items.map((item) => (
                <li
                  key={item.id}
                  className="expandable-list-item"
                  style={{
                    borderBottom: '1px solid #f0f0f0',
                    padding: '12px 16px'
                  }}
                >
                  <button
                    onClick={() => onItemClick(item.id)}
                    className="item-link"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#007bff',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      padding: 0,
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '4px',
                      display: 'block'
                    }}
                  >
                    {item.displayName}
                  </button>
                  <div
                    className="item-attributes"
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      marginTop: '4px'
                    }}
                  >
                    {Object.entries(item.attributes).map(([key, value]) => (
                      <span
                        key={key}
                        className="attribute"
                        style={{
                          fontSize: '12px',
                          color: '#6c757d',
                          background: '#f8f9fa',
                          padding: '2px 8px',
                          borderRadius: '3px'
                        }}
                      >
                        <strong>{key}:</strong> {Array.isArray(value) ? value.join(', ') : value}
                      </span>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
